/**
 * SN_SAI_TRM_REG_001_POP_001 — 챗봇에서 가져오기 (용어 후보) (2026-09-18, 2차 검토 확정)
 *
 * 챗봇이 못 알아들은 말(sai_dd_cand)이 자동으로 쌓인다. 여러 개 골라 팝업의 [적용] 을 누르면 화면이 용어로 가져온다(사용여부 N).
 * '유사 있음' 이 켜진 후보는 이미 비슷한 용어가 등록돼 있다는 뜻이다. 헤더 쓰레기통 = 무시.
 *
 * 조회조건 · 컬럼은 팝업 정의(sys_popupctrl · sys_popupgridcolumn, 08_AI_화면설정.sql)에서 온다. 조회는 sn_sai_trm_reg_001_pop_001_01.
 * 여러 줄 선택(rowSelection multiple) 그리드 + 헤더 쓰레기통(onBtnDeleteClick) = 무시. [적용] 은 getOkayData 로 고른 키를 화면에 돌려준다.
 * 본 : 거래처 SN_MAS_BAS_REG_001_POP_001 (getInitPopUpData · getOkayData) · 출하의뢰등록 SN_SAL_ISU_REG_001 상세정보 그리드(헤더 쓰레기통)
 */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import SearchPanel from "@widgets/SearchPanel";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnButton from "@shared/components/SnButton";
import { callApi, fnGetDataSet } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";

interface popupProps {
  modalRef?: any;
}

const SN_SAI_TRM_REG_001_POP_001 = forwardRef(({ modalRef }: popupProps, ref: React.Ref<any>) => {
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({ grid1: [] });
  const [dataSources, setDataSources] = useState<any>({ grid1: [] });

  useImperativeHandle(ref, () => ({
    /** [적용] — 고른 후보의 키를 화면으로 돌려준다. 없으면 팝업이 안내만 하고 닫히지 않는다 */
    getOkayData: async () => {
      const rows = gridRefs.current["grid1"].current?.getSelectedRows() || [];
      const keys = rows.map((r: any) => r.nm_word).filter(Boolean);
      if (keys.length === 0) {
        return { closeFlag: false, msgType: "alter", msgText: "가져올 후보를 선택해 주세요. 여러 개를 고를 수 있습니다." };
      }
      return { closeFlag: true, rtnData: keys };
    },
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitPopUpData("SN_SAI_TRM_REG_001_POP_001", {});
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({ grid1: rtn.grid.grid1 || [] });
        await onSearch(rtn.formData);
      } catch (error) {}
    };
    fetchform();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = async (cond?: any) => {
    try {
      const params = {
        id: "sn_sai_trm_reg_001_pop_001_01",
        params: cond ?? searchRef.current?.getFormdata?.() ?? {},
      };
      const data: any = await fnGetDataSet(params);
      setDataSources({ grid1: data?.[0] || [] });
    } catch (error: any) {}
  };

  /** 헤더 쓰레기통 — 고른 후보를 무시한다. 다시 올라오지 않는다 */
  const onIgnore = async () => {
    const rows = gridRefs.current["grid1"].current?.getSelectedRows() || [];
    const keys = rows.map((r: any) => r.nm_word).filter(Boolean);
    if (keys.length === 0) {
      await showDialog("alter", "무시할 후보를 선택해 주세요.");
      return;
    }
    const confirm = await showDialog("confirm", `${keys.length}건을 무시합니다. 다시 올라오지 않습니다.`);
    if (!confirm) return;
    const result = await callApi("SN_SAI_TRM_REG_001_btnIgnore", { words: keys });
    if (!result?.status) return;
    await onSearch();
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: { onBtnDeleteClick: onIgnore },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col relative">
      <div className="mb-1">
        <SearchPanel ref={searchRef} data={formData} controls={formControlData} />
      </div>
      <div className="w-full flex h-full min-h-0">
        <SnGrid
          ref={gridRefs.current["grid1"]}
          columnConfig={grids.grid1.columns}
          rowData={grids.grid1.dataSource}
          etcProps={{ rowSelection: "multiple", title: "후보", isEditable: false }}
          events={grids.grid1.events}
          header={
            <div>
              <SnButton text={"조회"} onClick={() => { onSearch(); }} />
            </div>
          }
        />
      </div>
    </div>
  );
});

export default SN_SAI_TRM_REG_001_POP_001;
